import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// Global back button — fixed top-left corner, shown on every route except the
// landing page ("/"). Clicking navigates one entry back in browser history
// when the previous entry is a safe same-origin VOOR BLACK page; otherwise
// falls back to the landing page. Never navigates to an external site,
// about:blank, or an unsafe scheme.
export default function BackArrow() {
  const location = useLocation();
  const navigate = useNavigate();
  // True once the user has navigated internally at least once. Set on any
  // pathname change after the first render.
  const sawInAppNav = useRef(false);
  const initialPathRef = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname !== initialPathRef.current) {
      sawInAppNav.current = true;
    }
  }, [location.pathname]);

  // Hidden on the landing page. Also hidden inside the Emergent OAuth
  // callback fragment where a full-screen loader is shown.
  if (location.pathname === "/" || location.pathname === "/admin" || location.pathname.startsWith("/admin/")) return null;

  const goBack = () => {
    // Safe-origin decision: only trust history back if we know the user
    // reached this route via an in-app navigation OR the referrer is a
    // same-origin http(s) URL from this app. Otherwise fall back to "/".
    const ref = document.referrer || "";
    let refSameOrigin = false;
    try {
      if (ref) {
        const u = new URL(ref);
        refSameOrigin =
          (u.protocol === "http:" || u.protocol === "https:") &&
          u.origin === window.location.origin;
      }
    } catch {
      refSameOrigin = false;
    }
    if (sawInAppNav.current || refSameOrigin) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <button
      type="button"
      onClick={goBack}
      data-testid="global-back-arrow"
      aria-label="Go back to the previous page"
      className="fixed z-[70] top-3 left-3 sm:top-4 sm:left-4 flex items-center justify-center rounded-full transition-colors"
      style={{
        width: "42px",
        height: "42px",
        background: "rgba(5, 5, 5, 0.72)",
        border: "1px solid rgba(214, 212, 0, 0.55)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        boxShadow: "0 4px 18px rgba(0,0,0,0.45)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(214, 212, 0, 0.95)";
        e.currentTarget.style.background = "rgba(214, 212, 0, 0.14)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(214, 212, 0, 0.55)";
        e.currentTarget.style.background = "rgba(5, 5, 5, 0.72)";
      }}
    >
      <ArrowLeft size={20} strokeWidth={2} className="text-[#d6d400]" />
    </button>
  );
}
